import React, { FunctionComponent, useState, useCallback } from "react"
import tw from "twin.macro"
import styled from "styled-components"

import { Form, Field } from "react-final-form"

import useTranslate from "logic/hooks/use-translate"
import { Button, Input, Textarea, Select } from "components"
import {
  onFormSubmit,
  requiredValidator,
  emailValidator,
  getQueryParam,
} from "logic/utils"
import { FORM_PLACEHOLDERS } from "model/messages"
import FormControl from "./FormControl"

const FormHeading = styled.h1`
  ${tw`text-white text-lg mb-4`}
`

const FormContainer = styled.div`
  ${tw`w-full lg:w-1/2 bg-dominant p-4 md:p-16 my-4 md:my-0 lg:mt-0 lg:pt-24 z-10`}
  /* min-height: 70rem; */
  ${tw`lg:mt-0 lg:pt-12`}

  height: 100%;
  --input-color: ${({ theme }) => theme.colors.gray[100]};
  --input-text-color: ${({ theme }) => theme.colors.gray[100]};
  --input-placeholder-color: ${({ theme }) => theme.colors.gray[100]};
  --input-focus-color: ${({ theme }) => theme.colors.yellow};
`

const FormGroupHeading = styled.h2`
  ${tw`text-lg my-6 md:my-8`}
`

const NameContainer = styled.div`
  ${tw`grid xl:gap-6 xl:grid-cols-2`}
`

const ButtonContainer = styled.div`
  ${tw`flex justify-end mt-8 lg:mb-8`}
`

const ContactForm: FunctionComponent = () => {
  const [isSubmitted, setIsSubmitted] = useState(false)
  const submitForm = useCallback(
    values =>
      onFormSubmit(values, "contact-form").then(() => setIsSubmitted(true)),
    []
  )
  const messages = useTranslate([
    "CONTACT_US",
    "PERSONAL_DATA",
    "TOPIC",
    "PROJECTS",
    "RENTALS",
    "OTHER",
    "OPERATIONS",
    "EVENTS",
    "THANK_YOU_MESSAGE",
    "SUBMIT",
    ...FORM_PLACEHOLDERS,
  ])

  return (
    <FormContainer className="contactFormContainer">
      <FormHeading>{messages.CONTACT_US}</FormHeading>
      {isSubmitted ? (
        messages.THANK_YOU_MESSAGE
      ) : (
        <Form
          onSubmit={submitForm}
          initialValues={{ REQUIREMEN: getQueryParam("requirement") }}
          render={({ handleSubmit, submitting, invalid }) => (
            <form onSubmit={handleSubmit} name="contact-form" data-netlify>
              {/* <FormGroupHeading>{messages.PERSONAL_DATA}</FormGroupHeading> */}
              <NameContainer>
                <Field name="FNAME" validate={requiredValidator}>
                  {({ input, meta }) => (
                    <FormControl
                      label="FIRST_NAME"
                      error={meta.error}
                      touched={meta.touched}
                    >
                      <Input
                        {...input}
                        type="text"
                        placeholder={messages.FIRST_NAME_PLACEHOLDER}
                      />
                    </FormControl>
                  )}
                </Field>
                <Field name="LNAME" validate={requiredValidator}>
                  {({ input, meta }) => (
                    <FormControl
                      label="LAST_NAME"
                      error={meta.error}
                      touched={meta.touched}
                    >
                      <Input
                        {...input}
                        type="text"
                        placeholder={messages.LAST_NAME_PLACEHOLDER}
                      />
                    </FormControl>
                  )}
                </Field>
              </NameContainer>
              <Field name="EMAIL" validate={emailValidator}>
                {({ input, meta }) => (
                  <FormControl
                    label="EMAIL"
                    error={meta.error}
                    touched={meta.touched}
                    alwaysLTR
                  >
                    <Input
                      {...input}
                      type="email"
                      placeholder={messages.EMAIL_PLACEHOLDER}
                    />
                  </FormControl>
                )}
              </Field>
              <Field name="PHONE" validate={requiredValidator}>
                {({ input, meta }) => (
                  <FormControl
                    label="PHONE"
                    error={meta.error}
                    touched={meta.touched}
                    alwaysLTR
                  >
                    <Input
                      {...input}
                      type="phone"
                      placeholder={messages.PHONE_PLACEHOLDER}
                    />
                  </FormControl>
                )}
              </Field>
              <Field name="COMPANYNAM">
                {({ input, meta }) => (
                  <FormControl
                    label="COMPANY_NAME"
                    error={meta.error}
                    touched={meta.touched}
                  >
                    <Input
                      {...input}
                      type="text"
                      placeholder={messages.COMPANY_PLACEHOLDER}
                    />
                  </FormControl>
                )}
              </Field>
              {/* <Field name="COMPANYWEB">
                {({ input, meta }) => (
                  <FormControl
                    label="COMPANY_WEBSITE"
                    error={meta.error}
                    touched={meta.touched}
                  >
                    <Input
                      {...input}
                      type="text"
                      placeholder={messages.COMPANY_WEBSITE_PLACEHOLDER}
                    />
                  </FormControl>
                )}
              </Field> */}
              {/* <FormGroupHeading>{messages.TOPIC}</FormGroupHeading> */}
              <Field name="REQUIREMEN" validate={requiredValidator}>
                {({ input, meta }) => (
                  <FormControl
                    label="REQUIREMENTS"
                    error={meta.error}
                    touched={meta.touched}
                  >
                    <Select {...input}>
                      <option value="" disabled hidden>
                        {messages.REQUIREMENT_PLACEHOLDER}
                      </option>
                      <option value="projects">{messages.PROJECTS}</option>
                      <option value="events">{messages.EVENTS}</option>
                      <option value="operations">{messages.OPERATIONS}</option>
                      <option value="rentals">{messages.RENTALS}</option>
                      <option value="other">{messages.OTHER}</option>
                    </Select>
                  </FormControl>
                )}
              </Field>
              <Field name="DETAILS" validate={requiredValidator}>
                {({ input, meta }) => (
                  <FormControl
                    label="DETAILS"
                    error={meta.error}
                    touched={meta.touched}
                  >
                    <Textarea
                      {...input}
                      placeholder={messages.DETAILS_PLACEHOLDER}
                      rows={5}
                    />
                  </FormControl>
                )}
              </Field>
              <ButtonContainer>
                <Button
                  type="submit"
                  isLoading={submitting}
                  disabled={submitting || invalid}
                >
                  {messages.SUBMIT}
                </Button>
              </ButtonContainer>
            </form>
          )}
        />
      )}
    </FormContainer>
  )
}

export default ContactForm
